'use client';

import { HospitalsSearchBox } from '@/features/hospital/HospitalsSearchBox';
import { FormProvider } from 'react-hook-form';
import {
  api_portalOfficeHospitalListing_responseFilter,
  api_portalOfficeHospitalListing_medicalSubject,
  api_portalOfficeHospitalListing_reportedAdditionalFee,
  ibow2_proto_RequestUser,
  ibow2_proto_PortalPatient,
  ibow2_proto_RequestUserFavorite,
} from '@/init/swagger';
import { HospitalSearchFilters } from '@/features/hospital/HospitalSearchFilters';
import { useLogic } from './logic';
import { SearchHospitalsType } from '@/schemas/SearchParamsSchemas/app/stations/search/hospitals/Schema';
import { HospitalSearchPanel } from '../HospitalSearchPanel';
import { Loading } from '@/components/loader/Loading';
import { PaginationInfo } from '@/helpers/searchHospitalListingsHelper';
import { HospitalListingOutput } from '@/helpers/parseHospitalListings';
import { HospitalMultiRequestFooter } from '../HospitalMultiRequestFooter';
import { HospitalSearchResultsForMultiRequest } from '../HospitalSearchResultsForMultiRequest';
import { HospitalMultiRequestNav } from '../HospitalMultiRequestNav';
import { Box } from '@mui/material';
import { EmptyBox } from '@/components/EmptyBox';
import { HospitalSearchResults } from '../HospitalSearchResults';
import { useMultiRequest } from './useMultiRequest';
import { HospitalSearchPanelForMultiRequest } from '../HospitalSearchPanelForMultiRequest';
import { ServerErrorSnackbar } from '../ServerErrorSnackbar';
import { ServerErrorInfo } from '@/helpers/serverErrorSnackbarHelper';
import { useCallback } from 'react';
import { ParsedAppStationsSearchPageSearchParams } from '@/schemas/SearchParamsSchemas/app/stations/search/Schema';
import { ParsedPublicStationsSearchPageSearchParams } from '@/schemas/SearchParamsSchemas/public/stations/search/Schema';

type Props = {
  formValues: SearchHospitalsType;
  hospitalSearchFilters: api_portalOfficeHospitalListing_responseFilter[];
  hospitalMedicalSubjects: api_portalOfficeHospitalListing_medicalSubject[];
  hospitalReportedAdditionalFees: api_portalOfficeHospitalListing_reportedAdditionalFee[];
  targetAddress: string;
  hospitalListings: HospitalListingOutput[];
  isPublicSearch?: boolean;
  requestUser?: ibow2_proto_RequestUser;
  paginationInfo: PaginationInfo;
  portalPatient: ibow2_proto_PortalPatient | undefined;
  favorites: Array<ibow2_proto_RequestUserFavorite>;
  allHospitalListings: HospitalListingOutput[][];
  serverError: ServerErrorInfo | null;
  parsedSearchParams:
    | ParsedAppStationsSearchPageSearchParams
    | ParsedPublicStationsSearchPageSearchParams;
};

export function HospitalsSearchForm({
  formValues,
  hospitalSearchFilters,
  hospitalMedicalSubjects,
  hospitalReportedAdditionalFees,
  targetAddress,
  hospitalListings,
  isPublicSearch = false,
  requestUser,
  paginationInfo,
  portalPatient,
  favorites,
  allHospitalListings,
  serverError,
  parsedSearchParams,
}: Props) {
  const {
    changeMode,
    method,
    hospitalMedicalCares,
    hospitalAppealPoints,
    search,
    isShowMap,
    toggleIsShowMap,
    gotoPageForPagination,
    localFavorites,
    setLocalFavorites,
    mapMarkerPortalOfficeId,
    setMapMarkerPortalOfficeIdWithoutScroll,
    handleMarkerClick,
    hospitalListingListRef,
  } = useLogic({
    formValues,
    hospitalSearchFilters,
    isPublicSearch,
    favorites,
    parsedSearchParams,
    hospitalListings,
  });

  const panelTitle = portalPatient
    ? `${portalPatient.last_name}${portalPatient.first_name}さんにマッチする医療機関`
    : '';

  const getCustomLink = useCallback(
    (listing: HospitalListingOutput) => {
      if (isPublicSearch || !portalPatient) return undefined;

      return `/app/hospitals/${listing.portal_office.id}?patient_id=${portalPatient.id}`;
    },
    [isPublicSearch, portalPatient],
  );

  const {
    multiRequestListings,
    multiRequestPage,
    setMultiRequestPage,
    handleSelectListing,
    matchInfo,
    selectInfo,
    multiRequestPaginationInfo,
    toggleAllSelection,
    isPending: isPendingForMultiRequest,
  } = useMultiRequest({
    allHospitalListings,
    portalPatient,
  });

  return (
    <>
      <ServerErrorSnackbar error={serverError} />
      <Loading isLoading={isPendingForMultiRequest} />
      <FormProvider {...method}>
        <form onSubmit={(event) => search({ event, keepPage: false })}>
          {parsedSearchParams.mode === 'search' && (
            <>
              <HospitalsSearchBox isPublicSearch={isPublicSearch} />
              <HospitalSearchFilters
                hospitalMedicalSubjects={hospitalMedicalSubjects}
                hospitalReportedAdditionalFees={hospitalReportedAdditionalFees}
                hospitalMedicalCares={hospitalMedicalCares}
                hospitalAppealPoints={hospitalAppealPoints}
                isPublicSearch={isPublicSearch}
              />
              <HospitalSearchPanel
                targetAddress={targetAddress}
                hospitalListings={hospitalListings}
                isShowMap={isShowMap}
                toggleIsShowMap={toggleIsShowMap}
                requestUser={requestUser}
                isPublicSearch={isPublicSearch}
                paginationInfo={paginationInfo}
                gotoPageForPagination={gotoPageForPagination}
                title={panelTitle}
                localFavorites={localFavorites}
                setLocalFavorites={setLocalFavorites}
                mapMarkerPortalOfficeId={mapMarkerPortalOfficeId}
                onMarkerClick={handleMarkerClick}
              >
                <>
                  {hospitalListings.length === 0 ? (
                    <Box sx={{ mx: 2 }}>
                      <EmptyBox
                        mainMessage="ご指定の条件に該当する医療機関が見つかりませんでした"
                        subMessage="検索条件を変更して、再度検索してください。"
                      />
                    </Box>
                  ) : (
                    <HospitalSearchResults
                      ref={hospitalListingListRef}
                      hospitalMedicalSubjects={hospitalMedicalSubjects}
                      hospitalSearchFilters={hospitalSearchFilters}
                      hospitalListings={hospitalListings}
                      isPublicSearch={isPublicSearch}
                      requestUser={requestUser}
                      localFavorites={localFavorites}
                      setLocalFavorites={setLocalFavorites}
                      getCustomLink={getCustomLink}
                      mapMarkerPortalOfficeId={mapMarkerPortalOfficeId}
                      setMapMarkerPortalOfficeIdWithoutScroll={
                        setMapMarkerPortalOfficeIdWithoutScroll
                      }
                      isShowMap={isShowMap}
                    />
                  )}
                </>
              </HospitalSearchPanel>
            </>
          )}
          {parsedSearchParams.mode === 'multiRequest' && (
            <>
              <HospitalMultiRequestNav
                onBackToSearch={() => changeMode('search')}
              />
              <HospitalSearchPanelForMultiRequest
                targetAddress={targetAddress}
                hospitalListings={multiRequestListings[
                  multiRequestPage - 1
                ]?.map(({ listing }) => ({ ...listing }))}
                isShowMap={isShowMap}
                toggleIsShowMap={toggleIsShowMap}
                requestUser={requestUser}
                title={panelTitle}
                localFavorites={localFavorites}
                setLocalFavorites={setLocalFavorites}
                allSelected={
                  multiRequestListings
                    .flat()
                    .filter(({ disabled }) => !disabled).length === 0
                    ? false
                    : multiRequestListings
                        .flat()
                        .filter(({ disabled }) => !disabled)
                        .every(({ checked }) => checked)
                }
                toggleAllSelection={toggleAllSelection}
                disabledToggleAllSelection={
                  multiRequestListings
                    .flat()
                    .filter(({ disabled }) => !disabled).length === 0
                }
                mapMarkerPortalOfficeId={mapMarkerPortalOfficeId}
                onMarkerClick={handleMarkerClick}
              >
                <HospitalSearchResultsForMultiRequest
                  hospitalMedicalSubjects={hospitalMedicalSubjects}
                  hospitalListings={multiRequestListings[multiRequestPage - 1]}
                  multiRequestPaginationInfo={multiRequestPaginationInfo}
                  onSelect={handleSelectListing}
                  onChangePagination={(_, page) => {
                    setMultiRequestPage(page);

                    // ページング時にListingListのスクロールを最上部に移動
                    if (hospitalListingListRef.current) {
                      hospitalListingListRef.current.scrollToListing(
                        0,
                        'click',
                      );
                    } else {
                      window.scrollTo(0, 0);
                    }

                    // ページング時に最初の病院を選択（マップ表示時のみ）
                    if (
                      isShowMap &&
                      multiRequestListings[page - 1] &&
                      multiRequestListings[page - 1].length > 0
                    ) {
                      const firstListing =
                        multiRequestListings[page - 1][0]?.listing;
                      const firstId = firstListing?.portal_office_id;
                      if (firstId) {
                        setMapMarkerPortalOfficeIdWithoutScroll(firstId);
                      }
                    }
                  }}
                  mapMarkerPortalOfficeId={mapMarkerPortalOfficeId}
                  setMapMarkerPortalOfficeIdWithoutScroll={
                    setMapMarkerPortalOfficeIdWithoutScroll
                  }
                  hospitalListingListRef={hospitalListingListRef}
                  isShowMap={isShowMap}
                  // favorites={favorites}
                />
              </HospitalSearchPanelForMultiRequest>
            </>
          )}
          {portalPatient && (
            <HospitalMultiRequestFooter
              mode={parsedSearchParams.mode}
              matchInfo={matchInfo}
              selectInfo={selectInfo}
              changeMode={changeMode}
            />
          )}
        </form>
      </FormProvider>
    </>
  );
}
